/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  TrackingCustomerData,
  TrackingPageContext,
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
} from '../../../types';
import {
  DetailedPageInfo,
  TrackingClientState,
  TrackingClientStateHolder,
  getShopId,
} from '../helpers/clientState';
import {exists} from '../helpers/exists';
import {
  SourceHistoryEventData,
  getSourceHistoryEventData,
} from '../helpers/sourceHistory';
import {stringifyEventData} from '../helpers/stringifyEventData';
import {trackLandingPage} from '../trackingHandlers/trackLandingPage';
import {shouldPushToDataLayerObject} from './datalayer';

export interface ContentView {
  shopVersion: string;
  shopId: string;
  timestamp: number;
  contentName: string;
  search: string;
  pageType: TrackingPageTypeWithId | TrackingPageTypeWithoutId;
  pageTypeId: number | null;
  breadcrumbs: string[];
  loadingTimeInSeconds: number;
  source: SourceHistoryEventData['source'];
  // origin click is string | null as if its null we dont push this field to data layer
  originClick: string | null;
}

export interface ContentViewGA4 {
  content_name: string;
  // document.title
  title: string;
  page_type: TrackingPageTypeWithId | TrackingPageTypeWithoutId;
  page_type_id: number | null;
  // origin click is string | null as if its null we dont push this field to data layer
  click_origin: string | null;
}

type OriginClickLandingPage = {
  type: string;
  id: string | number;
};

const MAX_PAGE_HISTORY_LENGTH = 4;

export const contentView =
  ({
    page,
    breadcrumbsPath,
    customer,
  }: {
    page: TrackingPageContext;
    breadcrumbsPath?: string | null;
    customer?: TrackingCustomerData;
  }) =>
  (clientState: TrackingClientStateHolder) => {
    let currentState = clientState.getCurrent();
    const startLoading = currentState.currentPage
      ? currentState.currentPage.timestamp
      : 0;
    const stopLoading = Date.now();

    const currentPage: DetailedPageInfo = {
      type: 'detailed',
      page,
      timestamp: stopLoading,
    };

    const historySize = currentState.pageHistory.length;

    const nextPageHistory =
      historySize >= MAX_PAGE_HISTORY_LENGTH
        ? [
            ...currentState.pageHistory.slice(
              historySize - (MAX_PAGE_HISTORY_LENGTH - 1),
              historySize
            ),
            currentPage,
          ]
        : [...currentState.pageHistory, currentPage];

    currentState = clientState.update({
      currentPage,
      pageHistory: nextPageHistory,
    });

    if (page.type === TrackingPageTypeWithId.Category) {
      currentState = clientState.update({
        lastVisitedCategory: {id: page.id, url: page.path},
      });
    }

    if (
      page.type === TrackingPageTypeWithId.ADP &&
      nextPageHistory.length > 1
    ) {
      const previousProductPage = nextPageHistory[nextPageHistory.length - 2];

      if (previousProductPage) {
        currentState = clientState.update({
          previousProductId: previousProductPage.page.id!,
        });
      }
    }

    const breadcrumbs: string[] = [];
    breadcrumbs.push(page.type);

    if (exists(breadcrumbsPath)) {
      breadcrumbsPath.split('/').forEach(part => {
        if (part !== '' && part !== 'c') {
          breadcrumbs.push(part);
        }
      });
    }

    const {source} = getSourceHistoryEventData({
      state: currentState,
    });

    const currentClientState = clientState.getCurrent();
    const originClick = getOriginClick({
      currentClientState,
      landingPage: {type: page.type, id: page.id || ''},
    });

    if (!currentState.landingPageTracked) {
      shouldPushToDataLayerObject(
        trackLandingPage(
          {
            ...currentState,
          },
          customer
        ),
        clientState
      );
    }
    currentState = clientState.update({
      landingPageTracked: true,
    });

    return {
      eventGA4: {
        event: 'content_view',
        content_name: page.path,
        title: page.title,
        page_type: page.type,
        page_type_id: page.id || null,
        click_origin: originClick,
      },
      eventGA3: {
        event: 'contentView',
        shopVersion: stringifyEventData([
          currentState.shop.appVersion,
          currentState.shop.gitHash,
        ]),
        shopId: getShopId(currentState.shop),
        timestamp: Date.now(),
        contentName: page.path,
        search: page.search,
        pageType: page.type,
        pageTypeId: page.id || null,
        breadcrumbs,
        loadingTimeInSeconds: (stopLoading - startLoading) / 1000,
        source,
        originClick,
      },
    };
  };

const getOriginClick = ({
  currentClientState,
  landingPage,
}: {
  currentClientState: TrackingClientState;
  landingPage: OriginClickLandingPage;
}) => {
  const pageHistory = currentClientState.pageHistory;
  const pageHistoryLength = pageHistory.length;
  const lastVisitedPage =
    pageHistoryLength === 1
      ? pageHistory[pageHistoryLength - 1]?.page
      : pageHistory[pageHistoryLength - 2]?.page;

  const origin = currentClientState.originClick;
  const isOriginClickEnabled =
    currentClientState.featureFlags.shouldTrackOriginClick;
  const isLandingPageTracked = currentClientState.landingPageTracked;

  const originClickFromHistory = [
    lastVisitedPage?.type,
    lastVisitedPage?.id,
    origin,
  ];
  const originClickOnLandingPage = [landingPage.type, landingPage.id, origin];
  const originClick = isLandingPageTracked
    ? originClickFromHistory
    : originClickOnLandingPage;

  return isOriginClickEnabled ? stringifyEventData(originClick) : null;
};
